import React, { useEffect, useState, useMemo } from 'react';
import { getSmsSummary, getSmsDaily, getSmsKeywords, getSmsConversations, getSmsCampaigns } from '../api';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { Line, Bar } from 'react-chartjs-2';
import ChartCard from './common/ChartCard';
import StatCard from './common/StatCard';
import LoadingBlock from './common/LoadingBlock';
import ErrorBlock from './common/ErrorBlock';
import SmsIcon from '@mui/icons-material/Sms';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { useTheme } from '@mui/material/styles';
import SpeedIcon from '@mui/icons-material/Speed';

function pct(n) { return (n * 100).toFixed(1) + '%'; }

export default function SmsReporting() {
  const theme = useTheme();
  const [startDate, setStartDate] = useState(dayjs().subtract(29, 'day'));
  const [endDate, setEndDate]     = useState(dayjs());
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  const [summary, setSummary]     = useState(null);
  const [daily, setDaily]         = useState(null);
  const [keywords, setKeywords]   = useState(null);
  const [convos, setConvos]       = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  const params = useMemo(() => ({
    startDate: startDate.format('YYYY-MM-DD'),
    endDate:   endDate.format('YYYY-MM-DD')
  }), [startDate, endDate]);

  const fetchAll = async () => {
    try {
      setLoading(true); setError(null);
      const [s, d, k, c, camp] = await Promise.all([
        getSmsSummary(params),
        getSmsDaily(params),
        getSmsKeywords(params),
        getSmsConversations(params),
        getSmsCampaigns(params),
      ]);
      setSummary(s); setDaily(d); setKeywords(k); setConvos(c); setCampaigns(camp);
    } catch (e) {
      console.error(e);
      setError('Failed to load SMS reporting');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const apply = () => fetchAll();

  if (loading && !summary) return <LoadingBlock height={320} />;
  if (error && !summary)   return <ErrorBlock message={error} onRetry={fetchAll} />;

  // Charts
  const dailyLine = daily ? {
    labels: daily.map(r => r.date),
    datasets: [
      { label: 'Sent', data: daily.map(r => r.sent),    borderColor: theme.palette.primary.main, backgroundColor: 'rgba(149,62,241,0.15)', fill: true, tension: 0.25, pointRadius: 0 },
      { label: 'Received', data: daily.map(r => r.received), borderColor: '#4CAF50', fill: false, tension: 0.25, pointRadius: 0 }
    ]
  } : null;

  const responseRateLine = daily ? {
    labels: daily.map(r => r.date),
    datasets: [
      { label: 'Response rate', data: daily.map(r => Math.round((r.responseRate || 0) * 1000)/10), borderColor: '#007AFF', fill: false, tension: 0.25, pointRadius: 0 }
    ]
  } : null;

  const keywordsBar = keywords ? {
    labels: ['STOP family', 'START / YES', 'HELP'],
    datasets: [{ label: 'Count', data: [keywords.stopCount||0, keywords.startCount||0, keywords.helpCount||0], backgroundColor: ['#e53935','#43a047','#fb8c00'] }]
  } : null;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper sx={{ p:2, display:'flex', gap:2, alignItems:'center', flexWrap:'wrap' }}>
          <DatePicker label="Start" value={startDate} onChange={d => d && setStartDate(d)} slotProps={{ textField: { size:'small' } }} />
          <DatePicker label="End"   value={endDate}   onChange={d => d && setEndDate(d)}     slotProps={{ textField: { size:'small' } }} />
          <Button variant="outlined" onClick={apply}>Apply</Button>
          {summary && (
            <Chip icon={<SmsIcon />} label={`Window: ${summary.start?.slice(0,10)} → ${summary.end?.slice(0,10)}`} />
          )}
        </Paper>
      </Grid>

      {/* KPIs */}
      {summary && (
        <>
          <Grid item xs={12} md={3}><StatCard title="Outbound sent" value={summary.sent} icon={<SmsIcon />} /></Grid>
          <Grid item xs={12} md={3}><StatCard title="Inbound received" value={summary.received} icon={<SmsIcon />} /></Grid>
          <Grid item xs={12} md={3}><StatCard title="Users messaged" value={summary.uniqueUsersMessaged} icon={<QueryStatsIcon />} /></Grid>
          <Grid item xs={12} md={3}><StatCard title="Users replied (≤24h)" value={summary.usersWhoRepliedWithinWindow} icon={<QueryStatsIcon />} /></Grid>

          <Grid item xs={12} md={3}><StatCard title="Response rate" value={pct(summary.responseRate || 0)} icon={<SpeedIcon />} /></Grid>
          <Grid item xs={12} md={3}><StatCard title="Median time to reply" value={`${Math.round(summary.medianTimeToFirstReplyMin)} min`} icon={<SpeedIcon />} /></Grid>
          <Grid item xs={12} md={3}><StatCard title="Opt-outs (STOP*)" value={summary.keywords?.stopCount || 0} /></Grid>
          <Grid item xs={12} md={3}><StatCard title="Re-opt-ins (START/YES)" value={summary.keywords?.startCount || 0} />
          </Grid>
        </>
      )}

      {/* Trends */}
      <Grid item xs={12} md={8}>
        <ChartCard title="Daily sent vs. received">
          {dailyLine ? <Line data={dailyLine} options={{ responsive:true, plugins:{ legend:{ position:'top' } }, scales:{ y:{ beginAtZero:true, ticks:{ precision:0 } } } }} /> : <Typography>No data</Typography>}
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <ChartCard title="Daily response rate (%)">
          {responseRateLine ? <Line data={responseRateLine} options={{ responsive:true, plugins:{ legend:{ display:false } }, scales:{ y:{ beginAtZero:true, suggestedMax:100 } } }} /> : <Typography>No data</Typography>}
        </ChartCard>
      </Grid>

      {/* Keywords */}
      <Grid item xs={12} md={6}>
        <ChartCard title="Compliance keywords">
          {keywordsBar ? <Bar data={keywordsBar} options={{ responsive:true, plugins:{ legend:{ display:false } }, scales:{ y:{ beginAtZero:true, ticks:{ precision:0 } } } }} /> : <Typography>No data</Typography>}
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartCard title="Top inbound keywords">
          <Box sx={{ p:2 }}>
            {keywords?.topKeywords?.length ? keywords.topKeywords.map((k) => (
              <Box key={k.keyword} sx={{ display:'flex', justifyContent:'space-between', py:0.5 }}>
                <Typography variant="body2" sx={{ pr:2, maxWidth:'70%', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{k.keyword}</Typography>
                <Typography variant="body2">{k.cnt}</Typography>
              </Box>
            )) : <Typography variant="body2" color="text.secondary">No inbound messages in range.</Typography>}
          </Box>
        </ChartCard>
      </Grid>

      {/* Conversations */}
      <Grid item xs={12} md={7}>
        <ChartCard title={`Conversation sessions (window ${convos?.windowMinutes || 30}m)`}>
          {convos?.daily?.length ? (
            <Bar data={{
              labels: convos.daily.map(r => r.day),
              datasets: [{ label:'Sessions', data: convos.daily.map(r => r.sessions), backgroundColor: theme.palette.primary.main }]
            }} options={{ responsive:true, plugins:{ legend:{ display:false } }, scales:{ y:{ beginAtZero:true, ticks:{ precision:0 } } } }} />
          ) : <Typography>No sessions</Typography>}
        </ChartCard>
      </Grid>
      <Grid item xs={12} md={5}>
        <ChartCard title="Longest sessions (top 20)">
          <Box sx={{ p:2, maxHeight: 360, overflow:'auto' }}>
            {convos?.longest?.length ? convos.longest.map((s, i) => (
              <Box key={`${s.userId}-${i}`} sx={{ display:'flex', justifyContent:'space-between', py:0.5 }}>
                <Typography variant="body2">User #{s.userId}</Typography>
                <Typography variant="body2">{s.messages} msgs · {s.durationMin} min</Typography>
              </Box>
            )) : <Typography variant="body2" color="text.secondary">No data</Typography>}
          </Box>
        </ChartCard>
      </Grid>

      {/* Campaigns (optional) */}
      <Grid item xs={12}>
        <ChartCard title="Campaign performance (reply ≤24h)">
          <Box sx={{ p:2, overflowX:'auto' }}>
            {campaigns?.length ? (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr><th align="left">Campaign</th><th align="right">Sent</th><th align="right">Unique users</th><th align="right">Repliers</th><th align="right">Response rate</th></tr>
                </thead>
                <tbody>
                  {campaigns.map(c => (
                    <tr key={String(c.campaign)}>
                      <td style={{ padding:'6px 4px' }}>{String(c.campaign ?? '(none)')}</td>
                      <td style={{ padding:'6px 4px' }} align="right">{c.sent}</td>
                      <td style={{ padding:'6px 4px' }} align="right">{c.uniqueUsers}</td>
                      <td style={{ padding:'6px 4px' }} align="right">{c.repliers}</td>
                      <td style={{ padding:'6px 4px' }} align="right">{pct(c.responseRate || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <Typography variant="body2" color="text.secondary">No campaign column detected or no data in range.</Typography>}
          </Box>
        </ChartCard>
      </Grid>
    </Grid>
  );
}


